import { Box, Link, Typography } from "@mui/material";
import ArrowForward from "@mui/icons-material/ArrowForward";
import type * as React from "react";
import GLLogo from "assets/images/GL-Logo.svg";
import CologuardLogo from "assets/images/cologuard/cologuard-logo.svg";
import TransitionArrow from "assets/images/cologuard/transition-arrow.png";
import GLButton from "components/v3/sharedComponents/GLButton";
import { COLORS } from "components/v3/Theme/colors";
import FootnoteSup from "./FootnoteSup";
import {
  NAVIGATION_EMAIL,
  NAVIGATION_MAILTO,
  NAVIGATION_PHONE_DISPLAY,
  NAVIGATION_PHONE_TEL
} from "./constants";

const publicPolicyLinkSx = {
  color: "inherit",
  fontFamily: "Inter, sans-serif",
  fontSize: "inherit",
  fontWeight: "inherit",
  letterSpacing: "inherit",
  lineHeight: "inherit",
  textDecoration: "underline",
  textUnderlineOffset: "2px"
} as const;

type GetStartedSectionProps = {
  onContinue: () => void;
};

const logoCardSx = {
  alignItems: "center",
  aspectRatio: "5 / 4",
  bgcolor: COLORS.BACKGROUND_BLUE,
  borderRadius: "24px",
  boxSizing: "border-box",
  display: "flex",
  flex: "1 1 0",
  height: "auto",
  justifyContent: "center",
  minWidth: 0,
  p: { md: 4, xs: 2 }
} as const;

const GetStartedSection: React.FC<GetStartedSectionProps> = ({
  onContinue
}) => (
  <Box
    component="section"
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: { md: 4, xs: 3 },
      width: "100%"
    }}
  >
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Typography component="h2" variant="body_strong_xl_black" sx={{ m: 0 }}>
        Ready to get started?
      </Typography>
      <Typography
        component="p"
        variant="body_black"
        sx={{ m: 0, maxWidth: { md: "66%", xs: "100%" } }}
      >
        If Cologuard sounds like the best option for you, continue by clicking
        the button below. After signing a Genomic Life form, you will be
        redirected to the Cologuard website to answer just a few more questions
        and request your test.<FootnoteSup>*</FootnoteSup>
      </Typography>
    </Box>

    <Box
      sx={{
        alignItems: { lg: "flex-end", xs: "stretch" },
        display: "flex",
        flexDirection: { lg: "row", xs: "column" },
        gap: 7
      }}
    >
      <Box
        sx={{
          display: "flex",
          flex: { lg: "4 1 0", xs: "none" },
          flexDirection: "column",
          gap: 4,
          minWidth: 0,
          pb: { lg: "30px", xs: 0 },
          width: { lg: "auto", xs: "100%" }
        }}
      >
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            flexWrap: "nowrap",
            justifyContent: "center",
            width: "100%",
            gap: 2
          }}
        >
          <Box sx={logoCardSx}>
            <Box
              alt="Genomic Life"
              component="img"
              src={GLLogo}
              sx={{
                height: "auto",
                maxHeight: "45%",
                maxWidth: "75%",
                objectFit: "contain",
                width: "auto"
              }}
            />
          </Box>
          <Box
            alt=""
            aria-hidden
            component="img"
            src={TransitionArrow}
            sx={{
              flexShrink: 0,
              height: "auto",
              // Keep overlap proportional to arrow width at every size
              mx: "calc(clamp(36px, 20%, 160px) * -0.45)",
              objectFit: "contain",
              position: "relative",
              width: "clamp(36px, 20%, 160px)",
              zIndex: 1
            }}
          />
          <Box
            onClick={onContinue}
            role="button"
            sx={{
              ...logoCardSx,
              border: "3px solid transparent",
              cursor: "pointer",
              position: "relative",
              transition: "transform 0.35s ease, border-color 0.2s ease",
              zIndex: 0,
              "&:hover": {
                borderColor: COLORS.ACCENT1_LIGHT,
                transform: "scale(1.05)"
              }
            }}
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onContinue();
              }
            }}
          >
            <Box
              alt="Cologuard by Exact Sciences"
              component="img"
              src={CologuardLogo}
              sx={{
                height: "auto",
                maxHeight: "55%",
                maxWidth: "75%",
                objectFit: "contain",
                width: "auto"
              }}
            />
          </Box>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <GLButton
            buttonType="primary"
            onClick={onContinue}
            rightIcon={<ArrowForward />}
            text="Continue to Cologuard"
          />
        </Box>
      </Box>

      <Box
        sx={{
          bgcolor: COLORS.ACCENT3_LIGHTER,
          borderRadius: "32px 32px 8px 32px",
          boxSizing: "border-box",
          flex: { lg: "1.3 1 0", xs: "none" },
          p: 3,
          width: { lg: "auto", xs: "100%" }
        }}
      >
        <Typography
          component="h3"
          variant="body_strong_black"
          sx={{ m: 0, mb: 1.5 }}
        >
          Need more information first?
        </Typography>
        <Typography component="p" variant="body_sm_black" sx={{ m: 0 }}>
          Our navigation team is happy to help! Simply send us an email at{" "}
          <Link href={NAVIGATION_MAILTO} sx={publicPolicyLinkSx}>
            {NAVIGATION_EMAIL}
          </Link>{" "}
          or request a callback at{" "}
          <Link href={NAVIGATION_PHONE_TEL} sx={publicPolicyLinkSx}>
            {NAVIGATION_PHONE_DISPLAY}
          </Link>
          .
        </Typography>
      </Box>
    </Box>
  </Box>
);

export default GetStartedSection;
